import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { gtag } from "../utils/gtag";
import { MAILTO_URL } from "../utils/constants";
import "./Packages.css";

type PackageSection = {
  day: string;
  items: string[];
};

type PackageOption = {
  title: string;
  price: string;
  tagline: string;
  includes?: string[];
  sections?: PackageSection[];
  serves: string;
  additionalGuests: string;
  notes: string[];
};

const packages: PackageOption[] = [
  {
    title: "The Whole Party Package",
    price: "£850",
    serves: "20 guests",
    tagline:
      "A complete, stress-free celebration package designed to elevate any occasion with effortless style.",
    includes: [
      "Catering for 20 guests (luxury grazing / buffet-style)",
      "Statement balloon hoop display",
      "Personalised welcome sign",
      "Drinks station setup",
    ],
    additionalGuests: "£20 per person",
    notes: [
      "Menu is a sample – full discussion on menu choices can be had at the point of booking.",
    ],
  },
  {
    title: "Baby Shower Package",
    price: "£899",
    serves: "20 guests",
    tagline:
      "An elegant and beautifully styled baby shower experience, perfect for creating unforgettable moments.",
    includes: [
      "Luxury afternoon tea for 20 guests",
      "Statement balloon hoop display",
      "Personalised welcome sign",
      "Full table styling (linen, décor, finishing touches)",
    ],
    additionalGuests: "£25 per person",
    notes: [
      "Includes bone china tea sets and full setup at your event. Optional event staff available at an additional charge.",
      "Contact us if you would also like us to provide full table styling and decorations.",
    ],
  },
  {
    title: "Hen Weekend Away Package",
    price: "£1,149",
    serves: "10 guests",
    tagline:
      "The ultimate luxury hen weekend experience—beautifully styled, effortlessly organised, and designed to create an unforgettable celebration for the bride-to-be and her closest friends.",
    sections: [
      {
        day: "Day One – Arrival Experience",
        items: [
          "Luxury grazing table for 10 guests",
          "Styled welcome drinks station",
          "Personalised welcome sign",
          "Statement balloon hoop display",
        ],
      },
      {
        day: "Day Two – Celebration Brunch & Afternoon Tea",
        items: [
          "Luxury breakfast grazing spread",
          "Traditional afternoon tea for 10 guests",
        ],
      },
    ],
    additionalGuests: "£65 per person",
    notes: [
      "Menu is a sample – full discussion on menu choices can be had at the point of booking.",
      "Includes full setup and styling for each element across both days. Travel and accommodation requirements outside of Bristol may incur additional fees.",
    ],
  },
];

const Packages = () => {
  return (
    <div className="packages-container">
      <Helmet>
        <title>Event Packages | Blossom and Graze Bristol – Grazing Boards & Catering</title>
        <meta
          name="description"
          content="Explore Blossom and Graze Bristol's event packages: The Whole Party Package from £850, Baby Shower Package from £899, and Hen Weekend Away Package from £1,599. All-inclusive celebration packages for 20 guests in Bristol."
        />
        <link rel="canonical" href="https://www.blossomandgraze.co.uk/packages" />
        <meta property="og:url" content="https://www.blossomandgraze.co.uk/packages" />
        <meta
          property="og:title"
          content="Event Packages | Blossom and Graze Bristol – Grazing Boards & Catering"
        />
        <meta
          property="og:description"
          content="All-inclusive celebration packages from £850 for 20 guests. Baby showers, hen weekends, and full party packages in Bristol."
        />
      </Helmet>

      <div className="packages-header">
        <span className="packages-header-label">All-Inclusive Celebrations</span>
        <div className="packages-header-divider" />
        <h1>Our Packages</h1>
        <p>Everything you need for an unforgettable occasion, beautifully taken care of</p>
      </div>

      <div className="packages-grid">
        {packages.map((pkg, index) => (
          <div key={index} className="package-card">
            <div className="package-card-top">
              <div className="package-card-header">
                <h2>{pkg.title}</h2>
                <div className="package-price-block">
                  <span className="package-price">{pkg.price}</span>
                  <span className="package-serves">serves {pkg.serves}</span>
                </div>
              </div>
              <p className="package-tagline">{pkg.tagline}</p>
            </div>

            <div className="package-card-body">
              {pkg.includes && (
                <div className="package-section">
                  <h3 className="package-section-title">Includes</h3>
                  <ul className="package-items">
                    {pkg.includes.map((item, i) => (
                      <li key={i}>
                        <span className="material-symbols-outlined package-item-icon">spa</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {pkg.sections &&
                pkg.sections.map((section, sIdx) => (
                  <div key={sIdx} className="package-section">
                    <h3 className="package-section-title">{section.day}</h3>
                    <ul className="package-items">
                      {section.items.map((item, i) => (
                        <li key={i}>
                          <span className="material-symbols-outlined package-item-icon">spa</span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}

              <p className="package-additional-guests">
                <span className="material-symbols-outlined package-guests-icon">group_add</span>
                Additional guests: {pkg.additionalGuests}
              </p>

              {pkg.notes.map((note, nIdx) => (
                <p key={nIdx} className="package-note">
                  {note}
                </p>
              ))}
            </div>

            <a
              href={MAILTO_URL}
              className="package-card-cta"
              onClick={() => {
                gtag("event", "enquire_click", {
                  link_location: "packages_card",
                  package_name: pkg.title,
                });
              }}
            >
              Enquire Now
            </a>
          </div>
        ))}
      </div>

      <div className="packages-bespoke">
        <div className="packages-bespoke-inner">
          <span className="packages-bespoke-label">Something Different?</span>
          <div className="packages-bespoke-divider" />
          <h2>Tailored Just for You</h2>
          <p>
            If these packages don&apos;t quite hit what you&apos;re looking for and you&apos;d like
            to tailor something else for your event, we&apos;d love to hear from you. We&apos;re
            always happy to create a bespoke experience that&apos;s perfectly suited to your
            occasion.
          </p>
          <div className="packages-bespoke-buttons">
            <a
              href={MAILTO_URL}
              className="packages-cta"
              onClick={() => {
                gtag("event", "enquire_click", {
                  link_location: "packages_bespoke",
                });
              }}
            >
              Get in Touch
            </a>
            <Link to="/menus" className="packages-cta-text">
              Browse Our Menus
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Packages;
